import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { RealtimeProvider } from './context/RealtimeContext'
import { SyncProvider } from './context/SyncContext'
import Layout from './components/Layout'
import ConnectionIndicator from './components/ConnectionIndicator'
import ConflictModal from './components/ConflictModal'

// Lazy load pages for code splitting
const Login = lazy(() => import('./pages/Login'))
const Tasks = lazy(() => import('./pages/Tasks'))
const TaskDetail = lazy(() => import('./pages/TaskDetail'))
const Clients = lazy(() => import('./pages/Clients'))
const ClientDetail = lazy(() => import('./pages/ClientDetail'))
const Session = lazy(() => import('./pages/Session'))
const EventDetail = lazy(() => import('./pages/EventDetail'))
const Money = lazy(() => import('./pages/Money'))
const ClientLoanHistory = lazy(() => import('./pages/ClientLoanHistory'))

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="text-crm-gold animate-pulse">Loading...</div>
    </div>
  )
}

/**
 * Protected route wrapper - redirects to login if not authenticated
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth()

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-crm-bg flex items-center justify-center">
        <div className="text-crm-gold animate-pulse">Loading...</div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return children
}

/**
 * Public route wrapper - redirects to app if already authenticated
 */
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-crm-bg flex items-center justify-center">
        <div className="text-crm-gold animate-pulse">Loading...</div>
      </div>
    )
  }

  // Redirect to app if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/clients" replace />
  }

  return children
}

/**
 * Main App Router
 */
function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/clients" replace />} />
          <Route path="clients" element={<Clients />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="money" element={<Money />} />
          <Route path="money/client/:clientId" element={<ClientLoanHistory />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="tasks/:id" element={<TaskDetail />} />
          <Route path="session" element={<Session />} />
          <Route path="events/:eventId" element={<EventDetail />} />
        </Route>

        {/* Catch all - redirect to clients (will then redirect to login if needed) */}
        <Route path="*" element={<Navigate to="/clients" replace />} />
      </Routes>
    </Suspense>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RealtimeProvider>
          <SyncProvider>
            <ConnectionIndicator />
            <ConflictModal />
            <AppRoutes />
          </SyncProvider>
        </RealtimeProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
